/**
 * Theme service for runtime theme switching
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { getTheme, getAvailableThemes } from './theme.config';
import type { Theme, ThemeColor } from './theme.types';

const STORAGE_KEY = 'crowdctrl-theme';

/** Public pages (login, about) lock document palette to this theme without changing stored preference. */
const LOCKED_PUBLIC_THEME = 'rmit-light';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<string | null>(null);
  public currentTheme$: Observable<string | null> = this.currentThemeSubject.asObservable();

  /** Nesting depth for {@link beginDocumentThemeOverride} / {@link endDocumentThemeOverride}. */
  private documentThemeOverrideDepth = 0;

  constructor() {
    // Initialize theme on service creation
    this.initializeTheme();
  }

  /**
   * Initialize theme from localStorage or system preference
   */
  private initializeTheme(): void {
    const savedTheme = this.mapLegacyTheme(localStorage.getItem(STORAGE_KEY));
    if (savedTheme && getTheme(savedTheme)) {
      this.applyTheme(savedTheme, false);
      localStorage.setItem(STORAGE_KEY, savedTheme);
      this.currentThemeSubject.next(savedTheme);
    } else {
      const defaultTheme = 'rmit-light';
      this.applyTheme(defaultTheme, false);
      this.currentThemeSubject.next(defaultTheme);
    }
  }

  /**
   * Set the active theme
   */
  setTheme(themeName: string): void {
    const theme = getTheme(themeName);
    if (!theme) {
      console.warn(`Theme "${themeName}" not found`);
      return;
    }

    this.applyTheme(themeName, true);
    localStorage.setItem(STORAGE_KEY, themeName);
    this.currentThemeSubject.next(themeName);
    this.reapplyLockedPublicThemeIfNeeded();
  }

  /**
   * While a public page (login, about) is visible, keep the document on the RMIT palette even if
   * the user changes their saved theme elsewhere. Does not touch localStorage or {@link currentTheme$}.
   */
  beginDocumentThemeOverride(themeName: string = LOCKED_PUBLIC_THEME): void {
    if (!getTheme(themeName)) {
      console.warn(`Theme "${themeName}" not found`);
      return;
    }
    this.documentThemeOverrideDepth++;
    this.applyThemeToDocument(themeName);
  }

  /**
   * Pair with {@link beginDocumentThemeOverride}. Restores the document to the user's saved theme
   * when the last nested override ends.
   */
  endDocumentThemeOverride(): void {
    if (this.documentThemeOverrideDepth === 0) {
      return;
    }
    this.documentThemeOverrideDepth--;
    if (this.documentThemeOverrideDepth > 0) {
      return;
    }
    const restore =
      this.currentThemeSubject.value ??
      this.mapLegacyTheme(localStorage.getItem(STORAGE_KEY)) ??
      LOCKED_PUBLIC_THEME;
    if (getTheme(restore)) {
      this.applyThemeToDocument(restore);
    }
  }

  /**
   * Apply theme to the document (classes + CSS variables). Optionally updates {@link currentTheme$}.
   */
  private applyTheme(themeName: string, persist: boolean): void {
    this.applyThemeToDocument(themeName);
    if (persist) {
      this.currentThemeSubject.next(themeName);
    }
  }

  /**
   * Applies palette + Ionic classes on `document.documentElement` only.
   */
  private applyThemeToDocument(themeName: string): void {
    const theme = getTheme(themeName);
    if (!theme) return;

    const html = document.documentElement;

    // Remove existing theme classes
    html.classList.remove(
      'theme-void',
      'theme-roblox-light',
      'theme-roblox-dark',
      'theme-arcade-light',
      'theme-arcade-dark',
      'theme-azure-depths',
      'theme-dreamgrove',
      'theme-rmit-light',
      'theme-pride',
      'theme-sunset-light',
      'theme-sunrise-light',
      'theme-default-light',
      'theme-default-dark',
    );

    // Add new theme class
    html.classList.add(`theme-${themeName}`);

    // Apply CSS variables
    this.applyCSSVariables(theme);

    // Apply dark mode class if needed
    if (theme.isDark) {
      html.classList.add('ion-theme-dark');
    } else {
      html.classList.remove('ion-theme-dark');
    }
  }

  private reapplyLockedPublicThemeIfNeeded(): void {
    if (this.documentThemeOverrideDepth > 0) {
      this.applyThemeToDocument(LOCKED_PUBLIC_THEME);
    }
  }

  /**
   * Apply CSS variables to document root
   */
  private applyCSSVariables(theme: Theme): void {
    const root = document.documentElement;

    // Apply all color variables
    Object.entries(theme.colors).forEach(([colorName, color]: [string, ThemeColor]) => {
      const prefix = `--ion-color-${colorName}`;

      root.style.setProperty(`${prefix}`, color.value);
      root.style.setProperty(`${prefix}-rgb`, `${color.rgb[0]}, ${color.rgb[1]}, ${color.rgb[2]}`);
      root.style.setProperty(`${prefix}-contrast`, color.contrast);
      root.style.setProperty(
        `${prefix}-contrast-rgb`,
        `${color.contrastRgb[0]}, ${color.contrastRgb[1]}, ${color.contrastRgb[2]}`,
      );
      root.style.setProperty(`${prefix}-shade`, color.shade);
      root.style.setProperty(`${prefix}-tint`, color.tint);
    });

    // Apply chrome variables
    if (theme.chrome) {
      root.style.setProperty('--app-chrome-bg', theme.chrome.background);
      root.style.setProperty('--app-chrome-text', theme.chrome.text);
      root.style.setProperty('--app-page-bg', theme.chrome.pageBackground);
      root.style.setProperty('--app-page-text', theme.chrome.pageText);
      root.style.setProperty('--app-heading-text', theme.chrome.headingText);
      root.style.setProperty('--app-tab-bg', theme.chrome.tabBackground);
      root.style.setProperty('--app-tab-text', theme.chrome.tabText);
      root.style.setProperty('--app-tab-text-selected', theme.chrome.tabTextSelected);
      root.style.setProperty('--app-muted-text', theme.chrome.mutedText);
    }

    // Apply loading card variables
    if (theme.loadingCard) {
      root.style.setProperty('--app-loading-card-background', theme.loadingCard.background);
      root.style.setProperty('--app-loading-card-text', theme.loadingCard.text);
    }
  }

  /**
   * Get current theme name
   */
  getCurrentTheme(): string | null {
    return this.currentThemeSubject.value || localStorage.getItem(STORAGE_KEY);
  }

  /**
   * Get current theme object
   */
  getCurrentThemeObject(): Theme | null {
    const themeName = this.getCurrentTheme();
    return themeName ? getTheme(themeName) || null : null;
  }

  /**
   * Get all available theme names
   */
  getAvailableThemes(): string[] {
    return getAvailableThemes();
  }

  private mapLegacyTheme(themeName: string | null): string | null {
    if (!themeName) return themeName;
    if (themeName.startsWith('roblox-')) {
      return themeName.replace('roblox-', 'arcade-');
    }
    return themeName;
  }

  /**
   * Toggle between light and dark variants of current theme
   */
  toggleDarkMode(): void {
    const current = this.getCurrentTheme();
    if (!current) return;

    const theme = getTheme(current);
    if (!theme) return;

    // Determine base theme name (without -light or -dark suffix)
    const baseName = current.replace(/-light$|-dark$/, '');
    const newThemeName = theme.isDark ? `${baseName}-light` : `${baseName}-dark`;

    if (getTheme(newThemeName)) {
      this.setTheme(newThemeName);
    }
  }

  /**
   * Check if current theme is dark
   */
  isDarkMode(): boolean {
    const theme = this.getCurrentThemeObject();
    return theme?.isDark || false;
  }
}
